import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Pause, Plus, XCircle } from "lucide-react";
import { EmployerLayout } from "@/components/employer/portal/EmployerLayout";
import { apiFetch, EmptyState, formatDate, Spinner, StatusPill } from "@/components/employer/portal/shared";

const STATUS_FILTERS = ["", "draft", "pending_review", "published", "paused", "closed", "rejected"];

export default function EmployerJobs() {
    const [jobs, setJobs] = useState(null);
    const [status, setStatus] = useState("");
    const [params] = useSearchParams();
    const navigate = useNavigate();

    const load = useCallback(async () => {
        const query = status ? `?status=${status}` : "";
        const res = await apiFetch(`/employer/jobs${query}`);
        if (res.ok) setJobs(await res.json());
    }, [status]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const edit = params.get("edit");
        if (edit) {
            navigate(`/employer/jobs/new?edit=${edit}`, { replace: true });
        }
    }, [params, navigate]);

    const action = async (id, name) => {
        await apiFetch(`/employer/jobs/${id}/${name}`, { method: "POST" });
        load();
    };

    return (
        <EmployerLayout
            title="Manage Jobs"
            subtitle="Create drafts, submit verified vacancies for review, and control live roles."
            actions={<a href="/employer/jobs/new" data-testid="jobs-new-button" className="inline-flex items-center gap-2 bg-forest px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:bg-forest-deep"><Plus size={14} /> New Job</a>}
        >
            <div className="mb-7 flex flex-wrap gap-2" data-testid="job-status-filters">
                {STATUS_FILTERS.map((item) => (
                    <button
                        key={item || "all"}
                        type="button"
                        data-testid={`job-filter-${item || "all"}`}
                        onClick={() => setStatus(item)}
                        className={`border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] transition-colors ${status === item ? "border-forest bg-forest text-ivory" : "border-ink/15 text-ink/55 hover:border-forest hover:text-forest"}`}
                    >
                        {item ? item.replace(/_/g, " ") : "All"}
                    </button>
                ))}
            </div>

            {!jobs ? <Spinner /> : jobs.length === 0 ? (
                <EmptyState
                    title="No vacancies yet"
                    copy="Create a draft with the role requirements, then submit it for Afrinexa review once your company is verified."
                    action={<a href="/employer/jobs/new" data-testid="empty-create-job" className="inline-flex items-center gap-2 bg-forest px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-ivory">Create Job <ArrowRight size={14} /></a>}
                />
            ) : (
                <div data-testid="employer-jobs-list" className="border-t border-ink/10">
                    {jobs.map((job) => (
                        <article key={job.id} data-testid={`employer-job-${job.id}`} className="grid gap-5 border-b border-ink/10 py-6 lg:grid-cols-[1fr_auto] lg:items-center">
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-3">
                                    <StatusPill status={job.status} />
                                    <span className="text-[10px] uppercase tracking-[0.18em] text-ink/40">{job.vacancies || job.positions} vacancies</span>
                                </div>
                                <h2 className="mt-3 font-serif text-2xl font-medium tracking-tight text-ink">{job.title}</h2>
                                <p className="mt-2 text-sm text-ink/55">
                                    {job.sector || job.category || "General"} · {job.city ? `${job.city}, ` : ""}{job.country} · Deadline {formatDate(job.application_deadline || job.deadline)}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <a href={`/employer/jobs/new?edit=${job.id}`} data-testid={`job-edit-${job.id}`} className="border border-ink/20 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink/60 transition-colors hover:border-forest hover:text-forest">Edit</a>
                                {["draft", "rejected", "paused"].includes(job.status) && (
                                    <button type="button" data-testid={`job-submit-${job.id}`} onClick={() => action(job.id, "submit")} className="bg-forest px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:bg-forest-deep">Submit</button>
                                )}
                                {job.status === "published" && (
                                    <button type="button" data-testid={`job-pause-${job.id}`} onClick={() => action(job.id, "pause")} className="inline-flex items-center gap-1.5 border border-ink/20 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink/60 hover:border-forest hover:text-forest"><Pause size={13} /> Pause</button>
                                )}
                                {job.status !== "closed" && (
                                    <button type="button" data-testid={`job-close-${job.id}`} onClick={() => action(job.id, "close")} className="inline-flex items-center gap-1.5 border border-ink/15 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink/45 hover:border-ink/40 hover:text-ink"><XCircle size={13} /> Close</button>
                                )}
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </EmployerLayout>
    );
}
